(function () {
  if (window.morph) return;

  const PRESERVED_ATTRIBUTES = ['open', 'aria-expanded', 'active', 'selected', 'checked'];

  const PRESERVED_MARKERS = ['data-product-store-scope', 'data-product-block-ready', 'data-card-media-ready'];
  const PRESERVED_ATTRIBUTES_SET = new Set(PRESERVED_ATTRIBUTES);

  const MORPH_OPTIONS = {
    childrenOnly: true,
    reject(oldNode, newNode) {
      if (newNode.nodeType === Node.TEXT_NODE && newNode.nodeValue?.trim() === '') {
        return true;
      }

      if (
        newNode instanceof HTMLTemplateElement &&
        newNode.shadowRootMode === 'open' &&
        oldNode.parentElement &&
        newNode.parentElement &&
        oldNode.parentElement.tagName === newNode.parentElement.tagName &&
        oldNode.parentElement?.shadowRoot != null
      ) {
        return true;
      }

      if (newNode.nodeType === Node.COMMENT_NODE && newNode.nodeValue === 'shopify:rendered_by_section_api') {
        return true;
      }

      return false;
    },
    onBeforeUpdate(oldNode, newNode) {
      if (!(oldNode instanceof Element) || !(newNode instanceof Element)) return;

      const oldAttrs = oldNode.attributes;
      for (let i = 0; i < oldAttrs.length; i++) {
        const attr =  (oldAttrs[i]);
        if (PRESERVED_ATTRIBUTES_SET.has(attr.name)) {
          const oldValue = attr.value;
          if (oldValue && oldValue !== newNode.getAttribute(attr.name)) {
            newNode.setAttribute(attr.name, oldValue);
          }
        }
      }

      for (let i = 0; i < PRESERVED_MARKERS.length; i++) {
        const name = PRESERVED_MARKERS[i];
        if (oldNode.hasAttribute(name) && !newNode.hasAttribute(name)) {
          newNode.setAttribute(name, oldNode.getAttribute(name));
        }
      }

      if (oldNode instanceof HTMLElement && newNode instanceof HTMLElement && oldNode.style.viewTransitionName) {
        newNode.style.viewTransitionName = oldNode.style.viewTransitionName;
      }
    },
    onAfterUpdate(node) {
      if (node instanceof HTMLElement && typeof node.morphedCallback === 'function') {
        queueMicrotask(() => node.morphedCallback());
      }
    },
  };

  function morph(oldTree, newTree, options = MORPH_OPTIONS) {
    if (!oldTree || !newTree) {
      throw new Error('Both oldTree and newTree must be provided');
    }

    if (typeof newTree === 'string') {
      const parsedNewTree = new DOMParser().parseFromString(newTree, 'text/html').body.firstChild;
      if (!parsedNewTree) {
        throw new Error('newTree string is not valid HTML');
      }
      newTree = parsedNewTree;
    }

    const scriptsBefore =
      options.activateScripts && oldTree instanceof Element ? new Set(oldTree.querySelectorAll('script')) : null;

    let result;
    if (options.childrenOnly) {
      updateChildren(newTree, oldTree, options);
      result = oldTree;
    } else if (newTree.nodeType === 11) {
      throw new Error('newTree should have one root node (not a DocumentFragment)');
    } else {
      result = walk(newTree, oldTree, options);
    }

    if (result instanceof Element) {
      if (scriptsBefore) activateInsertedScripts(result, scriptsBefore);
      recreateAppBlockScripts(result);
    }
    return result;
  }

  const EXECUTABLE_SCRIPT_TYPE = /^(|text\/javascript|application\/javascript|module)$/i;

  function activateInsertedScripts(container, scriptsBefore) {
    const inserted = Array.from(container.querySelectorAll('script')).filter(
      (script) => !scriptsBefore.has(script)
    );
    if (!inserted.length) return;

    const insertedSet = new Set(inserted);
    const loadedSources = new Set(
      Array.from(document.scripts)
        .filter((script) => script.src && !insertedSet.has(script))
        .map((script) => script.src)
    );

    for (const script of inserted) {
      if (!script.isConnected || !script.parentNode) continue;
      if (!EXECUTABLE_SCRIPT_TYPE.test((script.getAttribute('type') || '').trim())) continue;
      if (script.src && script.closest('.shopify-app-block')) continue;
      if (script.src && loadedSources.has(script.src)) continue;

      const fresh = document.createElement('script');
      for (const attr of Array.from(script.attributes)) {
        fresh.setAttribute(attr.name, attr.value);
      }
      if (script.textContent) fresh.textContent = script.textContent;

      if (script.src) loadedSources.add(script.src);
      script.replaceWith(fresh);
    }
  }

  function walk(newNode, oldNode, options) {
    if (!oldNode) return newNode;
    if (!newNode) return oldNode;

    if (newNode === oldNode) return oldNode;

    const newType = newNode.nodeType;
    if (newType !== oldNode.nodeType) return newNode;

    if (newType === 1 ) {
      const oldEl =  (oldNode);
      const newEl =  (newNode);
      if (oldEl.tagName === 'SHOPIFY-ACCELERATED-CHECKOUT-CART') return oldNode;
      if (newEl.tagName !== oldEl.tagName) return newNode;

      if (oldNode.isEqualNode(newNode)) {
        syncFormControlsInSubtree(newEl, oldEl);
        return oldNode;
      }

      const newKey = getNodeKey(newNode, options);
      const oldKey = getNodeKey(oldNode, options);
      if (newKey && oldKey && newKey !== oldKey) return newNode;

      if (oldEl.hasAttribute('data-skip-node-update') && newEl.hasAttribute('data-skip-node-update')) {
        updateChildren(newNode, oldNode, options);
      } else {
        updateNode(newNode, oldNode, options);
        updateChildren(newNode, oldNode, options);
      }
    } else {
      updateNode(newNode, oldNode, options);
    }

    options.onAfterUpdate?.(oldNode);

    return oldNode;
  }

  function updateNode(newNode, oldNode, options) {
    options.onBeforeUpdate?.(oldNode, newNode);

    const newType = newNode.nodeType;

    if (newType === 1 ) {
      const oldEl =  (oldNode);
      const newEl =  (newNode);
      if (!attributesEqual(oldEl, newEl)) {
        if (
          (newNode instanceof HTMLDetailsElement && oldNode instanceof HTMLDetailsElement) ||
          (newNode instanceof HTMLDialogElement && oldNode instanceof HTMLDialogElement)
        ) {
          if (!newNode.hasAttribute('declarative-open')) {
            newNode.open = oldNode.open;
          }
        }

        if (oldNode instanceof HTMLElement && newNode instanceof HTMLElement) {
          for (const attr of ['slot', 'sizes']) {
            const oldValue = oldNode.getAttribute(attr);
            const newValue = newNode.getAttribute(attr);
            if (oldValue !== newValue) {
              oldValue == null ? newNode.removeAttribute(attr) : newNode.setAttribute(attr, oldValue);
            }
          }
        }

        copyAttributes(newEl, oldEl);
      }

      syncFormControlState(newNode, oldNode);
    } else if (newType === 3  || newType === 8 ) {
      if (oldNode.nodeValue !== newNode.nodeValue) {
        oldNode.nodeValue = newNode.nodeValue;
      }
    }
  }

  function syncFormControlState(newNode, oldNode) {
    if (newNode instanceof HTMLInputElement && oldNode instanceof HTMLInputElement) {
      updateInput(newNode, oldNode);
    } else if (newNode instanceof HTMLOptionElement && oldNode instanceof HTMLOptionElement) {
      updateAttribute(newNode, oldNode, 'selected');
    } else if (newNode instanceof HTMLTextAreaElement && oldNode instanceof HTMLTextAreaElement) {
      updateTextarea(newNode, oldNode);
    }
  }

  function skipsNodeUpdate(newNode, oldNode) {
    return newNode.hasAttribute('data-skip-node-update') && oldNode.hasAttribute('data-skip-node-update');
  }

  function syncFormControlsInSubtree(newNode, oldNode) {
    if (!skipsNodeUpdate(newNode, oldNode)) {
      syncFormControlState(newNode, oldNode);
    }
    const newControls = newNode.querySelectorAll('input, option, textarea');
    if (newControls.length === 0) return;
    const oldControls = oldNode.querySelectorAll('input, option, textarea');
    for (let i = 0; i < newControls.length; i++) {
      const newControl =  (newControls[i]);
      const oldControl =  (oldControls[i]);
      if (skipsNodeUpdate(newControl, oldControl)) continue;
      syncFormControlState(newControl, oldControl);
    }
  }

  function attributesEqual(a, b) {
    const aAttrs = a.attributes;
    const bAttrs = b.attributes;
    const len = aAttrs.length;
    if (len !== bAttrs.length) return false;
    for (let i = 0; i < len; i++) {
      const aAttr =  (aAttrs[i]);
      const bAttr =  (bAttrs[i]);
      if (!aAttr.isEqualNode(bAttr)) return false;
    }
    return true;
  }

  function getNodeKey(node, options) {
    return options?.getNodeKey?.(node) ?? (node instanceof Element ? node.id : undefined);
  }

  function updateAttribute(newNode, oldNode, name) {
    if (newNode[name] !== oldNode[name]) {
      oldNode[name] = newNode[name];
      if (newNode[name]) {
        oldNode.setAttribute(name, '');
      } else {
        oldNode.removeAttribute(name);
      }
    }
  }

  function copyAttributes(newNode, oldNode) {
    const oldAttrs = oldNode.attributes;
    const newAttrs = newNode.attributes;

    for (const attr of Array.from(newAttrs)) {
      const { name: attrName, namespaceURI: attrNamespaceURI, value: attrValue } = attr;
      const localName = attr.localName || attrName;

      if (attrName === 'src' || attrName === 'href' || attrName === 'srcset' || attrName === 'poster') {
        if (oldNode.getAttribute(attrName) === attrValue) continue;
      }

      if (attrNamespaceURI) {
        const fromValue = oldNode.getAttributeNS(attrNamespaceURI, localName);
        if (fromValue !== attrValue) {
          oldNode.setAttributeNS(attrNamespaceURI, localName, attrValue);
        }
      } else {
        if (!oldNode.hasAttribute(attrName)) {
          oldNode.setAttribute(attrName, attrValue);
        } else {
          const fromValue = oldNode.getAttribute(attrName);
          if (fromValue !== attrValue) {
            if (attrValue === 'null' || attrValue === 'undefined') {
              oldNode.removeAttribute(attrName);
            } else {
              oldNode.setAttribute(attrName, attrValue);
            }
          }
        }
      }
    }

    for (const attr of Array.from(oldAttrs)) {
      if (attr.specified === false) continue;

      const { name: attrName, namespaceURI: attrNamespaceURI } = attr;
      const localName = attr.localName || attrName;

      if (attrNamespaceURI) {
        if (!newNode.hasAttributeNS(attrNamespaceURI, localName)) {
          oldNode.removeAttributeNS(attrNamespaceURI, localName);
        }
      } else if (!newNode.hasAttribute(attrName)) {
        oldNode.removeAttribute(attrName);
      }
    }
  }

  function updateInput(newNode, oldNode) {
    const newValue = newNode.value;

    updateAttribute(newNode, oldNode, 'checked');
    updateAttribute(newNode, oldNode, 'disabled');

    if (newNode.indeterminate !== oldNode.indeterminate) {
      oldNode.indeterminate = newNode.indeterminate;
    }

    if (oldNode.type === 'file') return;

    if (newValue !== oldNode.value) {
      oldNode.setAttribute('value', newValue);
      oldNode.value = newValue;
    }

    if (newValue === 'null') {
      oldNode.value = '';
      oldNode.removeAttribute('value');
    }

    if (!newNode.hasAttributeNS(null, 'value')) {
      oldNode.removeAttribute('value');
    } else if (oldNode.type === 'range') {
      oldNode.value = newValue;
    }
  }

  function updateTextarea(newNode, oldNode) {
    const newValue = newNode.value;
    if (newValue !== oldNode.value) {
      oldNode.value = newValue;
    }

    const firstChild = oldNode.firstChild;
    if (firstChild?.nodeType === Node.TEXT_NODE) {
      if (newValue === '' && firstChild.nodeValue === oldNode.placeholder) {
        return;
      }
      firstChild.nodeValue = newValue;
    }
  }

  function recreateAppBlockScripts(container) {
    const scripts = container.querySelectorAll('.shopify-app-block script[src]');

    for (const script of scripts) {
      if (!(script instanceof HTMLScriptElement)) continue;

      const parent = script.parentElement;
      if (!parent) continue;

      const newScript = document.createElement('script');
      for (const attr of Array.from(script.attributes)) {
        newScript.setAttribute(attr.name, attr.value);
      }
      if (script.textContent) {
        newScript.textContent = script.textContent;
      }

      script.remove();
      parent.appendChild(newScript);
    }
  }

  function updateChildren(newNode, oldNode, options) {
    if (
      oldNode instanceof Element &&
      oldNode.hasAttribute('data-skip-subtree-update') &&
      newNode instanceof Element &&
      newNode.hasAttribute('data-skip-subtree-update')
    ) {
      return;
    }

    const oldChildren = oldNode.childNodes;
    const newChildren = newNode.childNodes;
    const reject = options.reject;

    let oldChild, newChild, morphed, oldMatch;
    let offset = 0;

    for (let i = 0; ; i++) {
      oldChild = oldChildren[i];
      newChild = newChildren[i - offset];

      if (!oldChild && !newChild) {
        break;
      }

      if (!newChild) {
        oldChild && oldNode.removeChild(oldChild);
        i--;
        continue;
      }

      if (!oldChild) {
        oldNode.appendChild(newChild);
        offset++;
        continue;
      }

      if (same(newChild, oldChild, options)) {
        morphed = walk(newChild, oldChild, options);
        if (morphed !== oldChild) {
          oldNode.replaceChild(morphed, oldChild);
          offset++;
        }
        continue;
      }

      if (reject !== undefined && reject(oldChild, newChild)) {
        newNode.removeChild(newChild);
        i--;
        continue;
      }

      oldMatch = null;
      const oldChildrenLen = oldChildren.length;
      for (let j = i; j < oldChildrenLen; j++) {
        const potentialOldNode = oldChildren[j];
        if (potentialOldNode && same(potentialOldNode, newChild, options)) {
          oldMatch = potentialOldNode;
          break;
        }
      }

      if (oldMatch) {
        morphed = walk(newChild, oldMatch, options);
        if (morphed !== oldMatch) offset++;
        oldNode.insertBefore(morphed, oldChild);
      } else if (!getNodeKey(newChild, options) && !getNodeKey(oldChild, options)) {
        morphed = walk(newChild, oldChild, options);
        if (morphed !== oldChild) {
          oldNode.replaceChild(morphed, oldChild);
          offset++;
        }
      } else {
        oldNode.insertBefore(newChild, oldChild);
        offset++;
      }
    }
  }

  function same(a, b, options) {
    const aType = a.nodeType;
    const bType = b.nodeType;
    if (aType !== bType) return false;

    if (aType === Node.ELEMENT_NODE) {
      if (a instanceof Element && b instanceof Element && a.tagName !== b.tagName) return false;

      const aKey = getNodeKey(a, options);
      const bKey = getNodeKey(b, options);
      if (aKey && bKey && aKey !== bKey) return false;
    }

    if (aType === Node.TEXT_NODE && bType === Node.TEXT_NODE) {
      const av = a.nodeValue;
      const bv = b.nodeValue;
      return av === bv || av?.trim() === bv?.trim();
    }
    if (aType === Node.COMMENT_NODE && bType === Node.COMMENT_NODE) return a.nodeValue === b.nodeValue;

    return true;
  }

  window.morph = morph;
  window.MORPH_OPTIONS = MORPH_OPTIONS;

  window.MORPH_OPTIONS_WITH_SCRIPTS = { ...MORPH_OPTIONS, activateScripts: true };
})();
